<script lang="ts">
import { Field } from "vee-validate";
import { computed, useSlots } from "vue";
import { BField, BInput } from "buefy";

export default {
  components: { BField, BInput, Field },
  props: {
    name: {
      type: String,
      required: true,
    },
    rules: {
      type: String,
      required: false,
      default: "",
    },
    label: {
      type: String,
      required: true,
    },
    placeHolder: {
      type: String,
      required: true,
    },
    mandatory: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      required: true,
    },
    disable: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "text",
    },
    testid: {
      type: String,
      required: false,
      default: "",
    },
    /**
     * Si {@code true} , le label est rendu sur la MEME ligne que l'input
     * ( layout horizontal Bulma {@code .field.is-horizontal} ) au lieu
     * d'au-dessus . Tous les TextField marques {@code horizontal} dans
     * un meme formulaire alignent leurs inputs sur la meme colonne
     * verticale ( comportement natif Bulma : label-column 1/4 + control
     * flex-grow ) . Par defaut {@code false} pour garder le rendu
     * historique des formulaires existants .
     */
    horizontal: {
      type: Boolean,
      default: false,
    },
    /**
     * Si {@code true} , masque le message d'erreur sous le champ tout en
     * conservant la coloration danger ( bordure rouge + icone rouge a
     * la place du check vert ) . Utile quand un toast porte deja le
     * message metier et qu'un texte sous le champ ferait doublon visuel
     * ( ex : flow Creer un compte , erreur EXISTING_LOGIN signalee par
     * le toast , le champ Identifiant doit juste devenir rouge ) .
     */
    hideErrorMessage: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:value", "update:blur"],
  setup(props) {
    const rulesWithMandatory = computed(() => {
      let rulesWithMandatory = props.rules;
      if (props.mandatory) {
        rulesWithMandatory += rulesWithMandatory ? "|required" : "required";
      }
      return rulesWithMandatory;
    });
    const slots = useSlots();
    /**
     * {@code true} si le parent fournit un slot {@code actions} . Dans
     * ce cas , le rendu standard de la zone {@code .help} ( message
     * d'erreur sous l'input ) est remplace par un layout flex
     * space-between : message d'erreur a gauche , contenu du slot a
     * droite ( ex : lien "Renvoyer une cle" colle a droite , aligne
     * sur la meme ligne que le message d'erreur de validation ) .
     *
     * <p><b>Limitation</b> : la zone {@code .help} de Buefy ne s'affiche
     * que s'il y a un message d'erreur OU un compteur de caracteres .
     * Donc le slot {@code actions} disparait si aucune erreur n'est
     * presente . Pour un rendu TOUJOURS visible , utiliser
     * {@link #hasRightSlot} ( slot {@code right} ) qui se place a cote
     * de l'input via flex , independamment du message d'erreur .
     */
    const hasActionsSlot = computed(() => !!slots.actions);
    /**
     * {@code true} si le parent fournit un slot {@code right} . Le
     * contenu du slot est rendu TOUJOURS visible , directement a droite
     * de l'input via un flex container ( pas dans la zone help qui
     * disparait en l'absence d'erreur ) .
     *
     * <p>Usage typique : bouton / lien action qui doit etre cliquable
     * en permanence ( ex "Renvoyer une nouvelle cle par email" pour
     * le forgot-password ) .
     */
    const hasRightSlot = computed(() => !!slots.right);
    return { rulesWithMandatory, hasActionsSlot, hasRightSlot };
  },
};
</script>

<template>
  <Field
    v-slot="{ errors, meta, field }"
    :name="name"
    :rules="rulesWithMandatory"
    :vid="name"
  >
    <b-field
      :message="hasActionsSlot || hideErrorMessage ? undefined : errors[0]"
      :type="{
        'is-danger': errors && errors.length > 0,
        'is-success': meta.valid,
      }"
      :horizontal="horizontal"
      class="input-field"
      :aria-label="$t(label)"
    >
      <template #label>
        {{ $t(label) }}
        <span
          v-if="mandatory"
          class="required"
          :aria-label="$t('ponctuation.star')"
        > {{ $t("ponctuation.star") }}</span>
      </template>
      <!-- Slot custom de la zone .help : si le parent a fourni un slot
           'actions' , on remplace le rendu standard du message d'erreur
           par un layout flex permettant d'afficher l'erreur a gauche
           ET le contenu du slot a droite , sur la meme ligne . -->
      <template
        v-if="hasActionsSlot"
        #message
      >
        <div class="field-message-actions">
          <span class="field-message-actions__text">{{ errors[0] || "" }}</span>
          <slot name="actions" />
        </div>
      </template>
      <!-- Si un slot {@code right} est fourni : wrap input + slot dans un
           flex container pour TOUJOURS afficher le contenu du slot a droite
           de l'input ( independamment de la zone .help qui ne s'affiche que
           sur erreur ) . Sinon , rendu standard b-input direct . -->
      <div
        v-if="hasRightSlot"
        class="input-with-right"
      >
        <b-input
          class="input-with-right__input"
          :data-vv-as="name"
          :disabled="disable"
          :type="type"
          :password-reveal="type ==='password' "
          v-bind="field"
          :placeholder="$t(placeHolder)"
          :aria-label="$t(placeHolder)"
          :title="name"
          :data-testid="testid"
          @input="$emit('update:value', $event.target.value)"
          @blur="$emit('update:blur', $event.target.value)"
        />
        <div class="input-with-right__action">
          <slot name="right" />
        </div>
      </div>
      <b-input
        v-else
        :data-vv-as="name"
        :disabled="disable"
        :type="type"
        :password-reveal="type ==='password' "
        v-bind="field"
        :placeholder="$t(placeHolder)"
        :aria-label="$t(placeHolder)"
        :title="name"
        :data-testid="testid"
        @input="$emit('update:value', $event.target.value)"
        @blur="$emit('update:blur', $event.target.value)"
      />
    </b-field>
  </Field>
</template>

<style scoped lang="scss">
/*
 * Centrage natif Bulma . Toute regle {@code top: ...} override casse le
 * centrage flex applique par defaut sur {@code .icon.is-left / is-right} .
 * Seul {@code z-index: 0} reste necessaire pour eviter que l'icone passe
 * au-dessus d'un dropdown ouvert .
 */
.control.has-icons-left .icon,
.control.has-icons-right .icon {
  z-index: 0;
}

/*
 * Layout horizontal ( prop {@code horizontal=true} -> Bulma
 * {@code .field.is-horizontal} ) : par defaut , {@code .field-label}
 * dimensionne son flex-basis sur la taille du texte du label . Resultat :
 * les labels FR ( ex "Confirmation du nouvel email" ) sont plus larges
 * que EN ( "Confirmation of new email" ) -> les inputs ne s'alignent
 * plus verticalement d'une ligne a l'autre quand on change de langue ,
 * ni d'un formulaire a l'autre . On force ici un flex-basis fixe pour
 * que la colonne "label" soit constante , quel que soit le contenu
 * traduit . {@code text-align: left} aligne le texte sur le bord
 * gauche du conteneur ( Bulma default = {@code right} ) , conformement
 * a la maquette de reference .
 *
 * 12rem couvre les labels longs FR/EN courants des formulaires
 * concernes ( EditEmail / EditPwd / PasswordLostModal ) ; au-dela
 * le label wrap sur deux lignes , ce qui reste lisible .
 */
:deep(.field.is-horizontal) {
  .field-label {
    flex: 0 0 12rem;
    text-align: left;
    margin-right: 1.5rem;
  }
}

/*
 * Zone {@code .help} ( sous l'input ) en mode "actions slot" : erreur a
 * gauche , slot contenu a droite , meme ligne . {@code .field-message-actions__text}
 * absorbe l'espace residuel pour pousser le slot a l'extreme droite ;
 * couleur ROUGE conservee uniquement quand vee-validate a une erreur
 * ( buefy applique {@code .has-text-danger} sur la zone .help via
 * {@code :type="is-danger"} herite par cascade ) .
 */
.field-message-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}
.field-message-actions__text {
  flex: 1 1 auto;
}

/*
 * Layout flex pour le slot {@code right} : input prend la place
 * residuelle a gauche , slot ( typiquement un lien ) colle a droite
 * sur la meme ligne . {@code align-items: center} aligne verticalement
 * le lien avec le centre de l'input . {@code gap: 1rem} aere la
 * separation visuelle .
 *
 * {@code .input-with-right__input} doit utiliser {@code flex: 1} pour
 * occuper toute la largeur disponible , sinon l'input retrecit a sa
 * taille minimum ( comportement defaut Bulma ) .
 */
.input-with-right {
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
}
.input-with-right__input {
  flex: 1 1 auto;
  min-width: 0;
}
.input-with-right__action {
  flex: 0 0 auto;
  white-space: nowrap;
}
</style>
